import React, { useState, useRef } from 'react';
import { 
  Upload, Download, RefreshCw, AlertTriangle, CheckCircle,
  FileJson, Database, Clock, Shield, Trash2
} from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BackupPanel = ({ password, onBack }) => {
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showConfirmRestore, setShowConfirmRestore] = useState(false);
  const fileInputRef = useRef(null);

  const handleCreateBackup = async () => {
    setIsCreatingBackup(true);
    try {
      const response = await axios.post(`${API}/backup/create`, {}, {
        headers: { 'X-Admin-Password': password }
      });
      toast.success(response.data.message || 'Backup erstellt und per E-Mail gesendet!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des Backups');
    } finally {
      setIsCreatingBackup(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      toast.error('Nur JSON-Dateien werden akzeptiert');
      return;
    }

    setSelectedFile(file);
    setValidationResult(null);
    setShowConfirmRestore(false);

    // Validate the file
    setIsValidating(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/backup/validate`, formData, {
        headers: { 
          'X-Admin-Password': password,
          'Content-Type': 'multipart/form-data'
        }
      });

      setValidationResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Validierung');
      setValidationResult({ valid: false, error: error.response?.data?.detail });
    } finally {
      setIsValidating(false);
    }
  };

  const handleRestore = async () => {
    if (!selectedFile) return;

    setIsRestoring(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(`${API}/backup/restore`, formData, {
        headers: { 
          'X-Admin-Password': password,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(response.data.message || 'Backup erfolgreich wiederhergestellt!');
      setShowConfirmRestore(false);
      setSelectedFile(null);
      setValidationResult(null);
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Wiederherstellung');
    } finally {
      setIsRestoring(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unbekannt';
    try {
      return new Date(dateStr).toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const clearSelection = () => {
    setSelectedFile(null);
    setValidationResult(null);
    setShowConfirmRestore(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-orange-500 text-white p-6">
        <div className="max-w-4xl mx-auto">
          <button 
            onClick={onBack}
            className="text-blue-100 hover:text-white flex items-center gap-1 mb-2 text-sm"
          >
            ← Zurück zum Admin
          </button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="h-6 w-6" />
            Backup & Wiederherstellung
          </h1>
          <p className="text-blue-100">Datensicherung und Notfall-Wiederherstellung</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Clock className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-800">Automatisches Backup</p>
              <p className="text-sm text-blue-700">
                Jeden Tag um 00:00 Uhr wird automatisch ein Backup erstellt und an Ihre 
                E-Mail-Adresse gesendet. Sie können auch jederzeit manuell ein Backup erstellen.
              </p>
            </div>
          </div>
        </div>

        {/* Create Backup */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-green-600" />
              Backup erstellen
            </CardTitle>
            <CardDescription>
              Erstellt ein vollständiges Backup aller Daten und sendet es per E-Mail
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="flex-1 text-sm text-gray-600">
                <p>Das Backup enthält:</p>
                <ul className="list-disc list-inside mt-2 space-y-1 text-gray-500">
                  <li>Alle Mitglieder & Tiere</li>
                  <li>Buchungen & Fahrten</li>
                  <li>Buchhaltungsdaten (Einnahmen & Ausgaben)</li>
                  <li>Kalenderblockierungen</li>
                  <li>Einstellungen</li>
                </ul>
              </div>
              <Button
                onClick={handleCreateBackup}
                disabled={isCreatingBackup}
                className="bg-green-600 hover:bg-green-700 min-w-[200px]"
                data-testid="create-backup-btn"
              >
                {isCreatingBackup ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Wird erstellt...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Backup jetzt erstellen
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Restore Backup */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-orange-600" />
              Backup wiederherstellen
            </CardTitle>
            <CardDescription>
              Stellen Sie Ihre Daten aus einer Backup-Datei wieder her
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Warning */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800">Achtung!</p>
                  <p className="text-sm text-red-700">
                    Die Wiederherstellung überschreibt ALLE aktuellen Daten. Stellen Sie sicher, 
                    dass Sie zuerst ein aktuelles Backup erstellt haben.
                  </p>
                </div>
              </div>
            </div>

            {/* File Upload */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".json"
                className="hidden"
                id="backup-file"
              />
              
              {!selectedFile ? (
                <label 
                  htmlFor="backup-file" 
                  className="flex flex-col items-center cursor-pointer"
                >
                  <FileJson className="h-12 w-12 text-gray-400 mb-3" />
                  <p className="font-medium text-gray-700">Backup-Datei auswählen</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Klicken Sie hier oder ziehen Sie eine .json Datei hierher
                  </p>
                </label>
              ) : (
                <div className="text-center">
                  <FileJson className="h-12 w-12 text-blue-500 mx-auto mb-3" />
                  <p className="font-medium text-gray-700">{selectedFile.name}</p>
                  <p className="text-sm text-gray-500">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </p>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={clearSelection}
                    className="mt-2 text-red-600"
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Entfernen
                  </Button>
                </div>
              )}
            </div>

            {/* Validation Loading */}
            {isValidating && (
              <div className="flex items-center justify-center gap-2 py-4">
                <RefreshCw className="h-5 w-5 animate-spin text-blue-600" />
                <span className="text-gray-600">Validiere Backup-Datei...</span>
              </div>
            )}

            {/* Validation Result */}
            {validationResult && !isValidating && (
              <div className={`rounded-lg p-4 ${
                validationResult.valid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-start gap-3">
                  {validationResult.valid ? (
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className={`font-medium ${validationResult.valid ? 'text-green-800' : 'text-red-800'}`}>
                      {validationResult.valid ? 'Backup-Datei ist gültig' : 'Ungültige Backup-Datei'}
                    </p>
                    
                    {validationResult.valid && (
                      <div className="mt-3 space-y-2">
                        <p className="text-sm text-gray-600">
                          <strong>Backup-Datum:</strong> {formatDate(validationResult.backup_date)}
                        </p>
                        <p className="text-sm text-gray-600">
                          <strong>Gesamt:</strong> {validationResult.total_documents} Datensätze
                        </p>
                        <div className="mt-2">
                          <p className="text-sm font-medium text-gray-700 mb-1">Collections:</p>
                          <div className="grid grid-cols-2 gap-1 text-xs">
                            {Object.entries(validationResult.collections || {}).map(([name, info]) => (
                              <div key={name} className="flex justify-between bg-white/50 px-2 py-1 rounded">
                                <span className="text-gray-600">{name}</span>
                                <span className="font-medium">{info.count}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {validationResult.error && (
                      <p className="text-sm text-red-700 mt-1">{validationResult.error}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Restore Button */}
            {validationResult?.valid && !showConfirmRestore && (
              <Button
                onClick={() => setShowConfirmRestore(true)}
                className="w-full bg-orange-600 hover:bg-orange-700"
              >
                <Shield className="h-4 w-4 mr-2" />
                Wiederherstellung starten
              </Button>
            )}

            {/* Confirmation Dialog */}
            {showConfirmRestore && (
              <div className="bg-red-100 border-2 border-red-300 rounded-lg p-4">
                <p className="font-bold text-red-800 mb-2">Sind Sie sicher?</p>
                <p className="text-sm text-red-700 mb-4">
                  Alle aktuellen Daten werden durch das Backup vom {formatDate(validationResult?.backup_date)} ersetzt. 
                  Diese Aktion kann nicht rückgängig gemacht werden.
                </p>
                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowConfirmRestore(false)}
                    variant="outline"
                    className="flex-1"
                    disabled={isRestoring}
                  >
                    Abbrechen
                  </Button>
                  <Button
                    onClick={handleRestore}
                    className="flex-1 bg-red-600 hover:bg-red-700"
                    disabled={isRestoring}
                    data-testid="confirm-restore-btn"
                  >
                    {isRestoring ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Wird wiederhergestellt...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Ja, wiederherstellen
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Security Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-600" />
              Sicherheitshinweise
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-gray-600 space-y-2">
            <p>• Bewahren Sie Backup-Dateien an einem sicheren Ort auf</p>
            <p>• Backup-Dateien enthalten sensible Kundendaten</p>
            <p>• Testen Sie die Wiederherstellung regelmäßig mit Testdaten</p>
            <p>• Das tägliche automatische Backup erfolgt um 00:00 Uhr</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BackupPanel;
